/* eslint-disable no-unused-vars */
/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, message, Spin } from "antd";
import { CalendarComponent } from "../Master/CommonComponents";
import moment from "moment";
import { HubCPOverviewService } from "../../services/api/hubCPOverviewService";
import TopCard from "./TopCard";
import StatusDistribution from "./StatusDistribution";
import AgeingBuckets from "./AgeingBuckets";
import Exceptions from "./Exceptions";
import ShipmentDetailsModal from "./ShipmentDetailsModal";
import { TOP_CARD_NAMES } from "../../constants/hubManagerOverviewConstants";
import "./index.scss";

const HUB_OVERVIEW_DATE_FILTERS_KEY = "hub_overview_date_filters";

const parseStoredDateRange = (stored) => {
    if (!stored?.from || !stored?.to) return null;
    const from = moment(stored.from, "YYYY-MM-DD", true);
    const to = moment(stored.to, "YYYY-MM-DD", true);
    return from.isValid() && to.isValid() ? [from, to] : null;
};

const loadStoredDateFilters = () => {
    try {
        const raw = sessionStorage.getItem(HUB_OVERVIEW_DATE_FILTERS_KEY);
        if (!raw) {
            return { latestStatusDateRange: null, bookingDateRange: null };
        }
        const parsed = JSON.parse(raw);
        return {
            latestStatusDateRange: parseStoredDateRange(parsed.latestStatus),
            bookingDateRange: parseStoredDateRange(parsed.booking),
        };
    } catch (e) {
        return { latestStatusDateRange: null, bookingDateRange: null };
    }
};

const HubCPOverview = () => {
    const navigate = useNavigate();
    const [selectedHubs, setSelectedHubs] = useState(null);
    const [hubUser] = useState(() => {
        try {
            const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
            return user?.roles?.[0];
        } catch (e) {
            return null;
        }
    });
    const [dashboardData, setDashboardData] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [latestStatusDateRange, setLatestStatusDateRange] = useState(
        () => loadStoredDateFilters().latestStatusDateRange
    );
    const [bookingDateRange, setBookingDateRange] = useState(
        () => loadStoredDateFilters().bookingDateRange
    );
    const ALL_HUBS_VALUE = "ALL_HUBS";

    useEffect(() => {
        sessionStorage.setItem(
            HUB_OVERVIEW_DATE_FILTERS_KEY,
            JSON.stringify({
                latestStatus: {
                    from: latestStatusDateRange?.[0]?.format("YYYY-MM-DD") || null,
                    to: latestStatusDateRange?.[1]?.format("YYYY-MM-DD") || null,
                },
                booking: {
                    from: bookingDateRange?.[0]?.format("YYYY-MM-DD") || null,
                    to: bookingDateRange?.[1]?.format("YYYY-MM-DD") || null,
                },
            })
        );
    }, [latestStatusDateRange, bookingDateRange]);

    const getLatestStatusDates = () => ({
        fromDate: latestStatusDateRange?.[0] ? latestStatusDateRange[0].format("YYYY-MM-DD") : null,
        toDate: latestStatusDateRange?.[1] ? latestStatusDateRange[1].format("YYYY-MM-DD") : null,
    });

    const getBookingDates = () => ({
        bookingDateFrom: bookingDateRange?.[0] ? bookingDateRange[0].format("YYYY-MM-DD") : null,
        bookingDateTo: bookingDateRange?.[1] ? bookingDateRange[1].format("YYYY-MM-DD") : null,
    });



    // Transform dashboard data to card format
    const cards = useMemo(() => {
        if (!dashboardData || !Array.isArray(dashboardData)) {
            return [];
        }

        return dashboardData.map(item => ({
            id: item.title,
            name: item.title ? item.title.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) : "",
            value: item.totalCount || 0,
            visible: true,
            statuses: item.statuses || []
        }));
    }, [dashboardData]);

    // Initialize hub from active_hub or user premiseId
    useEffect(() => {
        const initializeHub = () => {
            try {
                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                if (activeHub?.hubId) {
                    setSelectedHubs(activeHub.hubId === "ALL_HUBS" ? null : activeHub.hubId);
                } else {
                    const userId = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userRole = JSON.parse(localStorage.getItem("userinfo") || "{}")?.roles?.[0];
                    const premiseId = userId?.userInfo?.premiseId;
                    if (premiseId && (userRole === "ROLE_HUBADMIN" || userRole === "ROLE_HUBOPERATOR")) {
                        setSelectedHubs(premiseId);
                    }
                }
            } catch (e) {
                console.error("Error initializing hub", e);
            }
        };
        initializeHub();
    }, []);

    const areAllHubsSelected = React.useMemo(() => {
        return selectedHubs === null || selectedHubs === "ALL_HUBS";
    }, [selectedHubs]);

    const hubParamForAPI = React.useMemo(() => {
        return areAllHubsSelected ? null : (selectedHubs ? [selectedHubs] : null);
    }, [selectedHubs, areAllHubsSelected]);

    // Listen for hub changes from AppHeader
    useEffect(() => {
        const handleGlobalHubChange = () => {
            try {
                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
                if (activeHub?.hubId) {
                    setSelectedHubs(activeHub.hubId === "ALL_HUBS" ? null : activeHub.hubId);
                } else {
                    setSelectedHubs(null);
                }
            } catch (e) {
                console.error("Error reading active_hub from localStorage", e);
            }
        };

        window.addEventListener("hubChanged", handleGlobalHubChange);
        return () => window.removeEventListener("hubChanged", handleGlobalHubChange);
    }, []);

    const abortControllerRef = React.useRef(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            const { signal } = abortControllerRef.current;

            try {
                setDashboardLoading(true);

                if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs === null) {
                    setDashboardLoading(false);
                    return;
                }

                const hubParam = areAllHubsSelected ? null : (selectedHubs ? selectedHubs : null);
                const { fromDate, toDate } = getLatestStatusDates();
                const { bookingDateFrom, bookingDateTo } = getBookingDates();
                const dateOptions = { signal, bookingDateFrom, bookingDateTo };

                const [dashboardResponse, pieChartResponse] = await Promise.all([
                    HubCPOverviewService.getHubOverviewDashboard(hubParam, fromDate, toDate, dateOptions),
                    HubCPOverviewService.getPieChartHubManagerOverview(hubParam, fromDate, toDate, dateOptions)
                ]);

                if (dashboardResponse?.data) {
                    setDashboardData(dashboardResponse.data);
                } else {
                    setDashboardData([]);
                }

                if (pieChartResponse) {
                    setPieChartData(pieChartResponse);
                } else {
                    setPieChartData([]);
                }
            } catch (error) {
                if (error.name === 'CanceledError' || error.code === "ERR_CANCELED") {
                    console.log("Request canceled");
                    return;
                }
                message.error("Failed to fetch dashboard data");
                console.error("Error fetching dashboard data:", error);
                setDashboardData([]);
            } finally {
                if (!signal.aborted) {
                    setDashboardLoading(false);
                }
            }
        };

        fetchDashboardData();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };

    }, [selectedHubs, areAllHubsSelected, latestStatusDateRange, bookingDateRange]);

    const handleCardClick = async (card) => {
        const cardName = card.name.trim();
        const hubIdToUse = areAllHubsSelected ? null : selectedHubs;
        const navigationState = {
            hubId: hubIdToUse,
            ...getLatestStatusDates(),
            ...getBookingDates(),
        };

        if (cardName.includes(TOP_CARD_NAMES.TOTAL_AVAILABLE_SHIPMENTS)) {
            navigate("/total-available-shipments-clickable-report", { state: navigationState });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.INBOUND_PENDENCY)) {
            navigate("/inbound-pendency-clickable-report", { state: navigationState });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_HUB)) {
            navigate("/outscan-pending-inscan-to-hub-clickable-report", { state: navigationState });
            return;
        }
        if (cardName.includes(TOP_CARD_NAMES.OUTSCAN_PENDING_INSCAN_TO_CP)) {
            navigate("/outscan-pending-inscan-to-cp-clickable-report", { state: navigationState });
            return;
        }
    };


    return (
        <div className="hub-cp-overview-container">
            {/* Unified Page Header + Filters */}
            <div className="page-topbar">
                <div className="topbar-left-content">
                    <span className="title-main">Pendency Overview</span>
                </div>
                <div className="topbar-right-content">
                    <div className="date-filter-item">
                        <span className="date-filter-label">Latest Status Date</span>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={latestStatusDateRange}
                            onChange={(value) => setLatestStatusDateRange(value)}
                            format="YYYY-MM-DD"
                            allowClear={true}
                            placeholder={["Start Date", "End Date"]}
                        />
                    </div>
                    <div className="date-filter-item">
                        <span className="date-filter-label">Booking Date</span>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={bookingDateRange}
                            onChange={(value) => setBookingDateRange(value)}
                            format="YYYY-MM-DD"
                            allowClear={true}
                            placeholder={["Start Date", "End Date"]}
                        />
                    </div>
                </div>
            </div>

            <Spin spinning={dashboardLoading}>
                <TopCard cards={cards} onCardClick={handleCardClick} loading={false} />

                <Row gutter={[16, 16]} className="dashboard-content-row">
                    <Col xs={24}>
                        <StatusDistribution
                            data={pieChartData}
                            selectedHubs={selectedHubs}
                            areAllHubsSelected={areAllHubsSelected}
                            latestStatusDateRange={latestStatusDateRange}
                            bookingDateRange={bookingDateRange}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <AgeingBuckets />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Exceptions />
                    </Col>
                </Row>
            </Spin>
        </div>
    );
};

export default HubCPOverview;
